import { useEffect } from "react";
import { useUser } from "@/hooks/useUser";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";

const REF_KEY = "bac_referral_code";
const INV_KEY = "bac_referral_invite_token";
const REF_COOKIE_DAYS = 30;

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getCookie(name: string): string | null {
  // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp
  // `name` is a hardcoded module constant (REF_KEY) and is regex-escaped
  // defensively above; no user input reaches this constructor.
  const match = document.cookie.match(new RegExp("(^|; )" + escapeRegex(name) + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function normalizeCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = String(raw).trim().toUpperCase();
  if (!/^[A-Z0-9]{4,32}$/.test(v)) return null;
  return v;
}

function normalizeToken(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = String(raw).trim();
  // base64url alphabet, length matches randomBytes(24).toString("base64url") = 32.
  if (!/^[A-Za-z0-9_-]{16,64}$/.test(v)) return null;
  return v;
}

function readStoredCode(): string | null {
  try {
    return normalizeCode(sessionStorage.getItem(REF_KEY)) || normalizeCode(getCookie(REF_KEY));
  } catch {
    return normalizeCode(getCookie(REF_KEY));
  }
}

function readStoredToken(): string | null {
  try {
    return normalizeToken(sessionStorage.getItem(INV_KEY)) || normalizeToken(getCookie(INV_KEY));
  } catch {
    return normalizeToken(getCookie(INV_KEY));
  }
}

function clearStoredReferral(): void {
  try { sessionStorage.removeItem(REF_KEY); } catch { /* noop */ }
  try { sessionStorage.removeItem(INV_KEY); } catch { /* noop */ }
  setCookie(REF_KEY, "", -1);
  setCookie(INV_KEY, "", -1);
}

export function trackReferralEvent(event: string, data?: Record<string, unknown>): void {
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void; posthog?: { capture?: (e: string, d?: unknown) => void } };
    if (typeof w.gtag === "function") w.gtag("event", event, data || {});
    if (w.posthog?.capture) w.posthog.capture(event, data || {});
  } catch { /* noop */ }
}

/** Global ref capture: reads `?ref=CODE` (and optional `?inv=TOKEN`) from the
 *  URL once, persists both, and attributes them to the current user once they
 *  become authenticated. If the invite token is recognised as expired by the
 *  server, the user is redirected to the resolution page and the stored
 *  values are cleared so signup doesn't silently attribute to a stale link. */
export function useReferralCapture(): void {
  const { user, session } = useUser();
  const referralsEnabled = useFeatureFlag("feature_referrals");

  // Capture from URL on every mount.
  useEffect(() => {
    if (!referralsEnabled) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("ref");
      const rawInv = params.get("inv");
      const code = normalizeCode(raw);
      const token = normalizeToken(rawInv);
      if (code) {
        sessionStorage.setItem(REF_KEY, code);
        setCookie(REF_KEY, code, REF_COOKIE_DAYS);
        trackReferralEvent("referral_link_clicked", { code, has_invite_token: !!token });
      }
      if (token) {
        sessionStorage.setItem(INV_KEY, token);
        setCookie(INV_KEY, token, REF_COOKIE_DAYS);
        // Validate token freshness up-front. If it's expired (or revoked),
        // send the user to the resolution page before they invest a minute
        // in signing up under a code that won't earn a credit anyway.
        const path = window.location.pathname;
        if (path !== "/refer-a-host/expired") {
          fetch(`${import.meta.env.BASE_URL}api/referrals/invite/check?token=${encodeURIComponent(token)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((payload) => {
              if (!payload) return;
              if (payload.expired || payload.status === "revoked") {
                clearStoredReferral();
                trackReferralEvent("referral_invite_expired_seen", { code, status: payload.status });
                const params = new URLSearchParams();
                if (payload.referrer_name) params.set("by", String(payload.referrer_name));
                if (payload.referrer_id) params.set("rid", String(payload.referrer_id));
                if (code) params.set("code", code);
                window.location.replace(`/refer-a-host/expired?${params.toString()}`);
              }
            })
            .catch(() => { /* network blip — keep the token, server will re-check at /attribute */ });
        }
      }
    } catch { /* noop */ }
  }, [referralsEnabled]);

  // Attribute once signed in.
  useEffect(() => {
    if (!referralsEnabled) return;
    const token = (session as { access_token?: string } | null | undefined)?.access_token;
    if (!user || !token) return;
    const code = readStoredCode();
    if (!code) return;
    const inv = readStoredToken();
    let cancelled = false;

    (async () => {
      try {
        if (cancelled) return;

        const res = await fetch(`${import.meta.env.BASE_URL}api/referrals/attribute`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ code, inv: inv || undefined }),
        });
        if (!res.ok) return;
        const payload = await res.json().catch(() => ({}));
        if (payload?.attributed) {
          trackReferralEvent("referral_signup_attributed", { code });
        } else if (payload?.reason === "invite_expired" || payload?.reason === "invite_revoked") {
          // Edge case: cookie survived past the server-side TTL — surface the
          // expired page so the user has a path back to the referrer.
          trackReferralEvent("referral_invite_expired_at_attribute", { code, reason: payload.reason });
          clearStoredReferral();
          window.location.assign(`/refer-a-host/expired?code=${encodeURIComponent(code)}`);
          return;
        }
        // Always clear once we've asked the server — success or not — to avoid loops.
        clearStoredReferral();
      } catch { /* noop */ }
    })();

    return () => { cancelled = true; };
  }, [user, session, referralsEnabled]);
}
